import { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Wordmark } from './WaveMark';
import { NAV_LINKS } from '../lib/site';

const MENU_INITIAL = { height: 0, opacity: 0 };
const MENU_ANIMATE = { height: 'auto', opacity: 1 };
const MENU_EXIT = { height: 0, opacity: 0 };
const MENU_TRANSITION = { duration: 0.3, ease: [0.22, 1, 0.36, 1] };

const DesktopLinks = () => (
  <div className="hidden lg:flex items-center gap-10">
    {NAV_LINKS.map((l) => (
      <a
        key={l.href}
        href={l.href}
        data-testid={`nav-link-${l.href.replace('#', '')}`}
        className="font-body text-[14px] font-medium text-[#94A3B8] hover:text-white transition-colors duration-300 tracking-wide"
      >
        {l.label}
      </a>
    ))}
    <a
      href="#contact"
      data-testid="nav-contact-cta"
      className="font-body font-semibold text-[13px] text-[#050B14] bg-[#00E5FF] hover:bg-white transition-colors duration-300 px-6 py-2.5 tracking-wider uppercase"
    >
      Contact Us
    </a>
  </div>
);

const MobileMenu = ({ onClose }) => (
  <motion.div
    initial={MENU_INITIAL}
    animate={MENU_ANIMATE}
    exit={MENU_EXIT}
    transition={MENU_TRANSITION}
    className="lg:hidden overflow-hidden bg-[#0A1628] border-t border-white/10"
    data-testid="mobile-menu"
  >
    <div className="px-6 py-6 flex flex-col gap-5">
      {NAV_LINKS.map((l) => (
        <a
          key={l.href}
          href={l.href}
          onClick={onClose}
          className="font-body text-base font-medium text-[#94A3B8] hover:text-white transition-colors duration-200"
          data-testid={`mobile-nav-link-${l.href.replace('#', '')}`}
        >
          {l.label}
        </a>
      ))}
      <a
        href="#contact"
        onClick={onClose}
        className="mt-2 text-center font-body font-semibold text-[13px] text-[#050B14] bg-[#00E5FF] px-6 py-3 uppercase tracking-wider"
        data-testid="mobile-contact-cta"
      >
        Contact Us
      </a>
    </div>
  </motion.div>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const onScroll = useCallback(() => setScrolled(window.scrollY > 60), [setScrolled]);
  const closeMenu = useCallback(() => setOpen(false), [setOpen]);

  useEffect(() => {
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [onScroll]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#050B14] border-b border-white/10 shadow-xl shadow-black/50'
          : 'bg-gradient-to-b from-[#050B14]/90 to-transparent'
      }`}
      data-testid="main-navbar"
    >
      <nav className="mx-auto max-w-7xl px-6 md:px-12 h-20 lg:h-36 flex items-center justify-between">
        <a href="#top" aria-label="Voyager Energy Partners home" data-testid="nav-home-link">
          <Wordmark />
        </a>

        <DesktopLinks />

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          aria-expanded={open}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>{open && <MobileMenu onClose={closeMenu} />}</AnimatePresence>
    </header>
  );
}
